import {distinctUntilChanged, filter, map, mergeMap, tap} from 'rxjs/operators';
import {Injectable, OnDestroy} from "@angular/core";
import {BehaviorSubject, Observable, of, Subject, throwError} from "rxjs";
import {LogCat, LogManager} from "../../../common/log";
import {HttpObservable} from "../../../common/";
import {UserContext} from "./user.context";
import {UserContextProxy} from "./user.context.proxy";
import {ResourceId} from "../../person.resource";
import {LoginService} from "../../login";
import {HttpDataPovider} from "../../../common/http/services/user.ctx.token";
import {
	IAttachmentOptions,
	IChatCredentials, ICustomWorklistFavorites,
	IDefaultResourceRoleAndLocation, IDocumentTogetherOptions,
	IFavoritesDefaultTabOptions,
	ILatestLocation, IPatientLetterOptions, ILinkingOptions,
	IMessagesOptions, IModalityAvailabilityViewerOptions,
	INavigationBarOptions,
	INotificationOptions,
	IPatientChartOptions, IPatientLogOptions,
	IPatientSearchOptions,
	IPatientSummaryOptions,
	ISchedulerOptions, IUserGroupDefaultWL,
	IWorkListOptions
} from "../interfaces/Iuser.preferences";
import {ILocation} from "../../org.struct/interfaces/favorite.locations";
import {IFavoriteStudy} from "../../procedures/interfaces/Ifavorite.study";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import * as _ from "lodash";
import {IUserContextWithAuth} from "..";
import {IPermissions} from "../../permissions";
import {IResourceRole} from "../interfaces/Iresource.role";
import {TimezoneService} from "../../../common/datetime/services/timezone.service";
import {IFavoriteWorklist, UserGroupFavoriteWorklists} from "../../worklists/interfaces/IFavoriteWorklist";
import {NotificationsService} from "../../notification/services/notification.service";
import {ICategoryWorklistsOrder} from "../../worklists/interfaces/Iworklist.order";
import {InavbarCustomWorklist} from "../../worklists/interfaces/Inavbar.custom.worklist";
import {PopupsPositions} from "../../../common/ui/components/popup/popup.component/popup.position.store";

//export  type CollectionOperation = "ADD" | "REMOVE";

export enum CollectionOperation {
	Add = "ADD",
	Remove = "REMOVE"
}

export interface ICustomWorklistChange{
	operation:CollectionOperation;
	worklistIds:number[];
}
/**
 ****************************************************************
 * Service class for holding the current user context information.
 *
 * Written by: Shai Zelman
 ****************************************************************
 */

@UntilDestroy()
@Injectable({ providedIn: 'root' })
  export class UserContextService implements OnDestroy {

	public subject: BehaviorSubject<UserContext>;

	public setFavoritesStudiesTask;
	public lastFavoritesStudies: IFavoriteStudy[];
	public pinStateObservable$:Observable<INavigationBarOptions>;
	public categoryStateObservable$:Observable<INavigationBarOptions>;

	public defaultWlStateObservable$:Observable<number>;
	public customWorklistsObervable$:Observable<ICustomWorklistChange>;

	private permissionsSubject: BehaviorSubject<IPermissions>;
	private pinStateSubject:Subject<INavigationBarOptions>;
	private categoryStateSubject:Subject<INavigationBarOptions>;
	private defaultWlStateSubject:Subject<number>;
	private customWorklistsChangeSubject:Subject<ICustomWorklistChange>;

	constructor(private userContextProxy: UserContextProxy,
				private loginService: LoginService,
				private httpProvider: HttpDataPovider,
				private timezoneService: TimezoneService) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "User context service created.");

		this.subject = new BehaviorSubject<UserContext>(null);
		this.permissionsSubject = new BehaviorSubject<IPermissions>(null);
		this.pinStateSubject =  new Subject<INavigationBarOptions>();
		this.categoryStateSubject =  new Subject<INavigationBarOptions>();
		this.defaultWlStateSubject = new Subject<number>();
		this.customWorklistsChangeSubject = new Subject<ICustomWorklistChange>();

		this.loginService.observeLogins().pipe(filter(userName => Boolean(userName))).pipe(untilDestroyed(this)).subscribe((userName: string) => {
			LogManager.debug(LogCat.RisUserContextCategories.General, "User login received for ", userName);

			this.updateUserContextFromServer();
			//console.log(`********** updateUserContextFromServer  favorites list :` ,this.getCurrFavoriteWorklists());
		});

		this.pinStateObservable$ = this.pinStateSubject.asObservable();
		this.categoryStateObservable$ = this.categoryStateSubject.asObservable();

		this.defaultWlStateObservable$ = this.defaultWlStateSubject.asObservable();
		this.customWorklistsObervable$ = this.customWorklistsChangeSubject.asObservable();
	}

	ngOnDestroy() {

	}

	public notifyPinState(data:INavigationBarOptions){
		this.pinStateSubject.next(data);
	}

	public notifyIsCategoryState(data:INavigationBarOptions){
		this.categoryStateSubject.next(data);
	}




	public notifyDefaultWlState(wlId:number){
		this.defaultWlStateSubject.next(wlId);
	}

	public permissionsObservable(){
		return this.permissionsSubject.asObservable().pipe(filter(x=>x!=null));
	}

	/**
	 * Gets the current user context.
	 * @param none
	 * @returns The current user context in UserContext format.
	 */
	public getCachedUserContext(): UserContext {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Get cached user context.");

		return this.subject.getValue();
	}

	public getCurrentWorkgroupId():number{
		return this.getCachedUserContext().roleGroupKey;
	}

	/**
	 * Updates the user context from the server.
	 * @param none
	 */
	public updateUserContextFromServer(): void {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Update the current user context from the server.");

		this.userContextProxy.getCurrentUserContext(this.timezoneService.getLocalOffsetFromUTC()).pipe(untilDestroyed(this)).subscribe(
			(userContextData: IUserContextWithAuth) => {
				LogManager.debug(LogCat.RisUserContextCategories.General, "The current user context arrived from the server.", userContextData);
				this.httpProvider.userGroup = userContextData.roleGroupKey;
				// load permissions before user context to avoid race detections when user context is ready -
				// shell observable is set to ready and then permission service tries to run before permissions are ready
				this.permissionsSubject.next(userContextData.userAuth);
				this.subject.next(new UserContext(<any>userContextData));

			}, (error: any) => {
				LogManager.error(LogCat.RisUserContextCategories.General, "Error updating the user context.", error);
			});
	}

	/**
	 * Get the user context from the server.
	 * @param none
	 */
	public getUserContext(): HttpObservable<UserContext> {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Get the current user context from the server.");

		if (this.subject.getValue()) {
			return this.subject.asObservable();
		}

		LogManager.error(LogCat.RisUserContextCategories.General,"Requested user context and not available.");
		this.userContextProxy.getCurrentUserContext(undefined).pipe(mergeMap((context: UserContext) => {
			this.subject.next(context);
			return this.subject.asObservable();
		}));
	}

	/**
	 * Gets an observable user context in IUserContext format.
	 * @param none
	 * @returns An observable containing an IUserContext object.
	 */
	public getUserContextObservable(): HttpObservable<UserContext> {
		return this.subject.asObservable().pipe(filter(x=>x!=null));
	}


	/**
	 * Gets an observable user context in IUserContext format.
	 * @param none
	 * @returns An observable containing an IUserContext object.
	 */
	public getGroupUpdatedObservable(): HttpObservable<UserContext> {
		return this.getUserContextObservable().pipe(
			distinctUntilChanged((ctx1: UserContext, ctx2: UserContext) => ctx1.roleGroupKey === ctx2.roleGroupKey));
	}

	/**
	 *
	 * @returns {HttpObservable<number>}
	 */
	public getCurrentOrganizationUpdatedObservable(): HttpObservable<UserContext> {
		return this.getUserContextObservable().pipe(
			distinctUntilChanged((ctx1: UserContext, ctx2: UserContext) => ctx1.currentOrganization === ctx2.currentOrganization));
	}

	/**
	 * Set a new active Group by the UserGroupKey
	 * @param newRoleGroupKey the UserGroupKey
	 */
	public selectRoleGroup(newRoleGroupKey: number): void {
		if (this.subject.value.groupKeys.some((roleGroupKey: number) => roleGroupKey === newRoleGroupKey)) {
			let newContext: UserContext = this.subject.value.updateContext({roleGroupKey: newRoleGroupKey});
			this.httpProvider.userGroup = newRoleGroupKey;
			this.subject.next(newContext);
		} else {
			LogManager.error(LogCat.RisUserContextCategories.General, "Tried to change user group to non permitted group " + newRoleGroupKey);
		}
	}

	/**
	 * Set a new active Role by the ResourceRoleKey
	 * @param newResourceRoleKey ResourceRoleKey
	 */
	public selectByResourceRole(newResourceRoleKey: number): void {
		let resourceIdKey: number = this.subject.value.personResource.resourceId.find((resourceId: ResourceId) => {
			return resourceId.resourceRoleKey === newResourceRoleKey;
		}).resourceIdKey;
		this.selectResourceRole(resourceIdKey);
	}

	/**
	 * Set a new active Role by the ResourceIdKey
	 * @param newResourceIdKey ResourceIdKey
	 */
	public selectResourceRole(newResourceIdKey: number): void {
		//TODO - make sure the group is in the allowed groups for the user
		if (this.subject.value.personResource.resourceId.some((resourceId: ResourceId) => resourceId.resourceIdKey === newResourceIdKey)) {
			let newContext: UserContext = this.subject.value.updateContext({resourceIDKey: newResourceIdKey});
			this.subject.next(newContext);
		} else {
			LogManager.error(LogCat.RisUserContextCategories.General, "tried to change user role to non permitted role " + newResourceIdKey);
		}
	}

	/**
	 * The function get requests to save the favorite list in the server.
	 * The function waiting the delay time and send the last request.
	 *
	 * @param favoritesStudies
	 * @param delay change the default value.
	 */
	public setFavoritesStudies(favoritesStudies: IFavoriteStudy[], delay ?: number) {
		this.lastFavoritesStudies = favoritesStudies;

		if (this.setFavoritesStudiesTask) {
			this.setFavoritesStudiesTask.cancel();
		}

		this.setFavoritesStudiesTask = _.debounce(() => {
			//Update user context.
			let userPreferences = this.subject.value.userPreferences;
			userPreferences.favoritesStudies = favoritesStudies;
			let newContext: UserContext = this.subject.value.updateContext({userPreferences: userPreferences});
			this.subject.next(newContext);

			this.userContextProxy.setFavoritesStudies(favoritesStudies).pipe(untilDestroyed(this)).subscribe();
		}, (delay >= 0) ? delay : 20 * 1000);

		this.setFavoritesStudiesTask(favoritesStudies);
	}

	public commitFavoritesStudiesTask() {
		//If the request not sent, cancel the request and send a new one immediately.
		if (this.setFavoritesStudiesTask && (this.subject.value.userPreferences.favoritesStudies !== this.lastFavoritesStudies)) {
			this.setFavoritesStudies(this.lastFavoritesStudies, 0);
		}
	}

	public setMessagesOptions(messagesOptions: IMessagesOptions) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set massages options start");

		this.userContextProxy.setMessagesOptions(messagesOptions).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("messagesOptions", messagesOptions);
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set massages options failed.", error);
			});
	}

	public setChatCredentialsOptions(chatCredentials: IChatCredentials) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set chat credentials start");

		this.userContextProxy.setChatCredentials(chatCredentials).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("chatCredentials", chatCredentials);
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set chat credentials failed.", error);
			});
	}

	public setFavoriteLocations(favoriteLocations: ILocation[]) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set favorite locations start");

		this.userContextProxy.setFavoriteLocations(favoriteLocations).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("favoriteLocations", favoriteLocations);
				LogManager.debug(LogCat.RisUserContextCategories.General, "Set favorite locations end");
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set favorite locations failed.", error);
			});
	}

	public processSaveResponseSuccess(keyName: string, keyVal: object) {
		//Update user preferences.
		let userPreferences = this.subject.value.userPreferences;
		userPreferences[keyName] = keyVal;
		let newContext: UserContext = this.subject.value.updateContext({userPreferences: userPreferences});
		this.subject.next(newContext);

		LogManager.debug(LogCat.RisUserContextCategories.General, "Set " + keyName + " end");
	}

	/**
	 * Sets current organization key of the user context object
	 * @param {number} key
	 */
	public setUserContextProperty(keyName: string, keyVal: any) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set " + keyName + " start");

		let userContext = {};
		userContext[keyName] = keyVal;
		let newContext: UserContext = this.subject.value.updateContext(userContext);
		this.subject.next(newContext);

		LogManager.debug(LogCat.RisUserContextCategories.General, "Set " + keyName + " end");
	}

	public setAttachmentOptions(attachmentOptions: IAttachmentOptions) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set attachment options start");

		this.userContextProxy.setAttachmentOptions(attachmentOptions).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("attachmentOptions", attachmentOptions);
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set attachment options failed.", error);
			});
	}

	public setFavoritesDefaultTab(favoritesDefaultTab: IFavoritesDefaultTabOptions) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set favorites default tab");

		this.userContextProxy.setFavoritesDefaultTab(favoritesDefaultTab).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("favoriteExamsDefaultTab", favoritesDefaultTab);
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set favorite exams default tab.", error);
			});
	}

	public setNavigationBarOptions(navigationBarOptions: INavigationBarOptions) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set navigation bar options start");
		return this.userContextProxy.setNavigationBarOptions(navigationBarOptions);
	}

	public setPatientSummaryOptions(patientSummaryOptions: IPatientSummaryOptions) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set patient summary options start");

		this.userContextProxy.setPatientSummaryOptions(patientSummaryOptions).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("patientSummaryOptions", patientSummaryOptions);
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set patient summary options failed.", error);
			});
	}

	public setSchedulerOptions(schedulerOptions: ISchedulerOptions) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set scheduler options start");

		this.userContextProxy.setSchedulerOptions(schedulerOptions).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("schedulerOptions", schedulerOptions);
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set scheduler options failed.", error);
			});
	}

	public setWorkListOptions(worklistOptions: IWorkListOptions) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set work list options start");

		this.userContextProxy.setWorklistOptions(worklistOptions).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("worklistOptions", worklistOptions);
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set work list options failed.", error);
			});
	}

	public setLinkingOptions(linkingOptions: ILinkingOptions) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set linking options start");

		this.userContextProxy.setLinkingOptions(linkingOptions).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("linkingOptions", linkingOptions);
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set linking options failed.", error);
			});
	}

	public setPatientChartOptions(patientChartOptions: IPatientChartOptions) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set patient chart options start");

		this.userContextProxy.setPatientChartOptions(patientChartOptions).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("patientChartOptions", patientChartOptions);
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set patient chart options failed.", error);
			});
	}

	public setDefaultUserLanguage(languageKey: number) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set default user language start.");

		this.userContextProxy.setDefaultUserLanguage(languageKey).pipe(untilDestroyed(this)).subscribe(() => {
				this.setUserContextProperty("defaultLangKey", languageKey);
				LogManager.debug(LogCat.RisUserContextCategories.General, "Set default user language end.");
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set default user language failed.", error);
			});
	}

	public setLastSelectedUserGroup(roleGroupKey: number) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set Last Selected User Group start.");

		this.userContextProxy.setLastSelectedUserGroup(roleGroupKey).pipe(untilDestroyed(this)).subscribe(() => {
				this.setUserContextProperty("roleGroupKey", roleGroupKey);
				LogManager.debug(LogCat.RisUserContextCategories.General, "Set Last Selected User Group end.");
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set Last Selected User Group failed.", error);
			});
	}

	public getUserHiddenGroups(): HttpObservable<number[]>{
		return this.userContextProxy.getUserHiddenGroups();
	}

	public setDefaultResourceRole(defaultResourceRoleAndLocation: IDefaultResourceRoleAndLocation) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set default resource role start.");

		this.userContextProxy.setDefaultResourceRole(defaultResourceRoleAndLocation).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("defaultResourceRole", defaultResourceRoleAndLocation.defaultResourceRole);
				this.processSaveResponseSuccess("latestLocation", defaultResourceRoleAndLocation.latestLocation);
				LogManager.debug(LogCat.RisUserContextCategories.General, "Set default resource role end.");
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set default resource role failed.", error);
			});
	}

	public getCurrentResourceRole(): Observable<IResourceRole>  {
		return this.subject.asObservable().pipe(map(ctx => {
			return ctx.resourceRoleList.find(r => r.resourceIdKey === ctx.userPreferences.defaultResourceRole.resourceIdKey);
		}));
	}

	public setLatestLocation(latestLocation: ILatestLocation) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set latest location start.");

		this.userContextProxy.setLatestLocation(latestLocation).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("latestLocation", latestLocation);
				LogManager.debug(LogCat.RisUserContextCategories.General, "Set latest location end.");
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set latest location failed.", error);
			});
	}

	public setPatientSearchOptions(patientSearchOptions: IPatientSearchOptions) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set patient search options start");

		this.userContextProxy.setPatientSearchOptions(patientSearchOptions).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("patientSearchOptions", patientSearchOptions);
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set patient search options failed.", error);
			});
	}

	public setDocumentTogetherOptions(documentTogetherOptions: IDocumentTogetherOptions) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set document together options start");

		this.userContextProxy.setDocumentTogetherOptions(documentTogetherOptions).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("documentTogetherOptions", documentTogetherOptions);
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set document together options failed.", error);
			});
	}

	public setDefaultPatientClass(patientClassKey: number) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set default patient class start.");

		this.userContextProxy.setDefaultPatientClass(patientClassKey).pipe(untilDestroyed(this)).subscribe(() => {
				this.setUserContextProperty("defaultPatientClassKey", patientClassKey);
				LogManager.debug(LogCat.RisUserContextCategories.General, "Set default patient class end.");
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set default patient class failed.", error);
			});
	}

	public setDefaultModalityScheduleGroup(modalityScheduleGroupKey: number) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set default patient class start.");

		this.userContextProxy.setDefaultModalityScheduleGroup(modalityScheduleGroupKey).pipe(untilDestroyed(this)).subscribe(() => {
			this.setUserContextProperty("defaultModalityScheduleGroupKey", modalityScheduleGroupKey);
			LogManager.debug(LogCat.RisUserContextCategories.General, "Set default modality schedule group end.");
		},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set default modality schedule group failed.", error);
			});
	}
	public setNotificationOptions(notificationOptions: INotificationOptions) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set notification options start");

		this.userContextProxy.setNotificationOptions(notificationOptions).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("notificationOptions", notificationOptions);
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set notification options failed.", error);
			});
	}


	/*
	* Add Worklist. to Favorites
	* @param {number} key
	* */
	public addWorklistToFavorites(key: number): Observable<any> {
		LogManager.debug(LogCat.RisUserContextCategories.FavoriteWorklists, "UserContextService.addWorklistToFavorite");
		let workGroupId = this.getCurrentWorkgroupId();
		//get the favoriteWorklists from the cached userPreferences object and map it to array of {worklistId:number}
		let worklists: IFavoriteWorklist[] = this.getCurrFavoriteWorklists(workGroupId)
			.map(worklist => {
				return {worklistId: worklist.worklistId};
			});
		//add the current worklist to the array of favorites that will be sent to the server.
		worklists.unshift({worklistId: key});
		return this.setFavoriteWorklists(worklists,workGroupId, key, true);
	}

	/*
	* Remove Worklist from Favorites
	* @param {number} key
	* */
	public removeWorklistFromFavorites(key: number): Observable<any> {
		LogManager.debug(LogCat.RisUserContextCategories.FavoriteWorklists, "OrganizationStructureComponent.removeOrgFromFavorite");
		let workGroupId = this.getCurrentWorkgroupId();
		let worklists: IFavoriteWorklist[] = this.getCurrFavoriteWorklists(workGroupId).filter(worklist => worklist.worklistId !== key); //TODO:check
		return this.setFavoriteWorklists(worklists,workGroupId, key, false);
	}

	// /**
	//  * Gets the list of favorite worklists that is stored in the cached userPreferences in the service.
	//  * @returns {IFavoriteWorklist[]}
	//  */
	public getCurrFavoriteWorklists(workGroupId:number): IFavoriteWorklist[] {
		let worklists = null;
		let userGroupsFavoriteWorklists  = this.getCachedUserContext().userPreferences.userGroupsFavoriteWorklists;
		if (userGroupsFavoriteWorklists){
			worklists = userGroupsFavoriteWorklists[workGroupId] ?? [];
		}else{
			worklists = [];
		}
		return worklists;
	}

	public getLoginDataPopupPositions() {
		return this.getCachedUserContext().userPreferences.popupPosition;
	}

	public getUpdatedUserGroupsFavoriteWorklistsObject(favoriteWorklists: IFavoriteWorklist[], workGroupId: number){
		let userGroupsFavoriteWorklists  = this.getCachedUserContext().userPreferences.userGroupsFavoriteWorklists;
		if (!userGroupsFavoriteWorklists){
			userGroupsFavoriteWorklists = {};
		}
		userGroupsFavoriteWorklists[workGroupId] = favoriteWorklists;
		return userGroupsFavoriteWorklists;
	}

	public getUpdatedWorklistsOrderObject(favoriteWorklists: number[], workGroupId: number,categoryName:string){
		let worklistsOrder  = this.getCachedUserContext().userPreferences.worklistsOrder;
		if (!worklistsOrder) {
			worklistsOrder = {};
		}
		if (!worklistsOrder[workGroupId]) {
			worklistsOrder[workGroupId] = {};
		}
		worklistsOrder[workGroupId][categoryName] = favoriteWorklists;
		return worklistsOrder;
	}

	public getUpdatedNavbarCustomWorklistsObject(worklist: InavbarCustomWorklist, operation: CollectionOperation) {
		const singleWorklistArray:InavbarCustomWorklist[] = [worklist];
		return this.getUpdatedNavbarCustomWorklistsObjectMulti(singleWorklistArray,operation);
	}

	public getUpdatedNavbarCustomWorklistsObjectMulti(worklists: InavbarCustomWorklist[], operation: CollectionOperation) {
		let customWorklists = this.getCachedUserContext().userPreferences.navbarCustomWorklists;
		if (!customWorklists) {
			customWorklists = [];
		}
		worklists.forEach(worklist => {
			const isItemInCollection: boolean = customWorklists.some(elm => elm.worklistId === worklist.worklistId);
			if (operation === "ADD") {
				if (!isItemInCollection) {
					customWorklists.push(worklist);
				}
			} else {
				if (isItemInCollection) {
					customWorklists = customWorklists.filter(elm => elm.worklistId !== worklist.worklistId);
				}
			}

		});
		return customWorklists;
	}

	public updateFavoriteWorklistsCahedValue(favoriteWorklists: number[]){
		let workGroupId = this.getCurrentWorkgroupId();
		let favoriteWorklistsToStore = favoriteWorklists.map(item => ({worklistId:item}));
		const updatedUserGroupsFavoriteWorklistsObject = this.getUpdatedUserGroupsFavoriteWorklistsObject(favoriteWorklistsToStore,workGroupId);
		this.processSaveResponseSuccess("userGroupsFavoriteWorklists", updatedUserGroupsFavoriteWorklistsObject);
	}

	/*public updatedWorklistsOrderCahedValue(favoriteWorklists: number[],categoryName:string){
		let workGroupId = this.getCurrentWorkgroupId();
		const updatedWorklistsOrderObject = this.getUpdatedWorklistsOrderObject(favoriteWorklists,workGroupId,categoryName);
		//console.log('updatedWorklistsOrderObject before ',JSON.stringify(updatedWorklistsOrderObject));
		this.processSaveResponseSuccess("worklistsOrder", updatedWorklistsOrderObject);
		//console.log('updatedWorklistsOrderObject after ',JSON.stringify(updatedWorklistsOrderObject));
	}*/


	public setFavoriteWorklists(favoriteWorklists: IFavoriteWorklist[],workGroupId: number, worklistId: number,
								isFavorite: boolean): HttpObservable<any> {
		LogManager.debug(LogCat.RisUserContextCategories.FavoriteWorklists, "Set favorite worklists start");
		const updatedUserGroupsFavoriteWorklistsObject = this.getUpdatedUserGroupsFavoriteWorklistsObject(favoriteWorklists,workGroupId);
		this.processSaveResponseSuccess("userGroupsFavoriteWorklists", updatedUserGroupsFavoriteWorklistsObject);
		let userGroupFavoriteWorklists: UserGroupFavoriteWorklists = {
			worGroupId:workGroupId,
			favoriteWorklists:favoriteWorklists
		};
		return this.userContextProxy.setFavoriteWorklists(userGroupFavoriteWorklists).pipe(
			tap(() => {
				LogManager.debug(LogCat.RisUserContextCategories.FavoriteWorklists, "Set favorite worklists end");
			}),
			map(() => ({
				worklistId: worklistId,
				isFavorite: isFavorite
			}))
		);
	}
	public setFavoriteWorklistsOrder(favoriteWorklists: IFavoriteWorklist[]){
		let workGroupId = this.getCurrentWorkgroupId();
		const updatedUserGroupsFavoriteWorklistsObject = this.getUpdatedUserGroupsFavoriteWorklistsObject(favoriteWorklists,workGroupId);
		this.processSaveResponseSuccess("userGroupsFavoriteWorklists", updatedUserGroupsFavoriteWorklistsObject);
		let userGroupFavoriteWorklists: UserGroupFavoriteWorklists = {
			worGroupId:workGroupId,
			favoriteWorklists:favoriteWorklists
		};
		return this.userContextProxy.setFavoriteWorklists(userGroupFavoriteWorklists).pipe(
			tap(() => {
				LogManager.debug(LogCat.RisUserContextCategories.FavoriteWorklists, "Set favorite worklists end");
			})
		);
	}


	public setDefaultWorklist(defaultWlId: number){
		let workGroupId = this.getCurrentWorkgroupId();
		let userGroupDefaultWl: IUserGroupDefaultWL = {
			workGroupId:workGroupId,
			workListId:defaultWlId
		};
		return this.userContextProxy.setDefaultWorklist(userGroupDefaultWl).pipe(
			tap(() => {
				LogManager.debug(LogCat.RisUserContextCategories.Proxy, "Save default worklist end");
			})
		);
	}


	public setCategoryWorklistOrder(categoryName : string,worklistsOrder :number[]): HttpObservable<any> {

		LogManager.debug(LogCat.RisWorklistCategories.WorklistOrder, "Set favorite worklists order for category start");
		let workGroupId = this.getCurrentWorkgroupId();
		const updatedUserGroupsFavoriteWorklistsObject = this.getUpdatedWorklistsOrderObject(worklistsOrder,workGroupId,categoryName);
		this.processSaveResponseSuccess("worklistsOrder", updatedUserGroupsFavoriteWorklistsObject);
		const postedObject :ICategoryWorklistsOrder = {
			workGroupId,
			categoryName,
			worklistsOrder
		};
		return this.userContextProxy.setCategoryWorklistOrder(postedObject).pipe(
			tap(() => {
				LogManager.debug(LogCat.RisWorklistCategories.WorklistOrder, "Set favorite worklists order for category end");
			})
		);
	}

	//Only for testing purposes
	public  deleteWorklistOrder(): HttpObservable<void> {
		return this.userContextProxy.deleteWorklistOrder();
	}
	//Only for testing purposes
	public deleteDynamicWorklist(): HttpObservable<void> {
		return this.userContextProxy.deleteDynamicWorklist();
	}

	public addNavBarCustomWorklist(worklistId: number): HttpObservable<void> {
		const worklist = {worklistId};
		const worklistsObject: InavbarCustomWorklist[] = this.getUpdatedNavbarCustomWorklistsObject(worklist, CollectionOperation.Add);

		const customWlChangeData = {operation: CollectionOperation.Add, worklistIds: [worklistId]};
		this.customWorklistsChangeSubject.next(customWlChangeData);

		return this.userContextProxy.addNavBarCustomWorklist(worklist).pipe(tap(() => {
			this.processSaveResponseSuccess("navbarCustomWorklists", worklistsObject);
		}));

		/*

			this.userContextProxy.setCustomWorklistFavorites(customWLFavorites).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("customWLFavorites", customWLFavorites);
				LogManager.debug(LogCat.RisUserContextCategories.CustomWorklistFavorites, "Set Custom Worklist Favorites end");
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.CustomWorklistFavorites, "Set favorite Custom Worklist Favorites failed.", error);
			});
		 */
	}

	public removeNavBarCustomWorklist(worklistId: number): HttpObservable<void> {
		const worklist = { worklistId };
		const worklistsObject: InavbarCustomWorklist[] = this.getUpdatedNavbarCustomWorklistsObject(worklist, CollectionOperation.Remove);
		const customWlChangeData = {operation: CollectionOperation.Remove, worklistIds: [worklistId]};
		this.customWorklistsChangeSubject.next(customWlChangeData);

		return this.userContextProxy.removeNavBarCustomWorklist(worklist).pipe(tap(()=> {
			this.processSaveResponseSuccess("navbarCustomWorklists", worklistsObject);
		}));
	}

	// public removeNavBarCustomWorklists(worklistsIdArray: number[]): HttpObservable<void> {
	// 	const worklists = worklistsIdArray.map(x => <InavbarCustomWorklist>({worklistId:x}));
	// 	const worklistsObject: InavbarCustomWorklist[] = this.getUpdatedNavbarCustomWorklistsObjectMulti(worklists, CollectionOperation.Remove);
	// 	this.processSaveResponseSuccess("navbarCustomWorklists", worklistsObject);
	// 	return this.userContextProxy.removeNavBarCustomWorklists(worklists).pipe(tap(()=> {
	// 		const customWlChangeData = {operation: CollectionOperation.Remove, worklistIds: worklistsIdArray};
	// 		this.customWorklistsChangeSubject.next(customWlChangeData);
	// 	}));
	// }


	public setPatientLogOptions(patientLogOptions: IPatientLogOptions) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set patient log options start");

		this.userContextProxy.setPatientLogOptions(patientLogOptions).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("patientLogOptions", patientLogOptions);
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set patient log options failed.", error);
			});
	}

	/*
    * Checks if favorite worklists contain current key.
	* @param key
    * @returns {boolean}
    * */
	public isFavorite(worklistId: number): boolean {
		let workGroupId = this.getCurrentWorkgroupId();
		let index = this.getCurrFavoriteWorklists(workGroupId).findIndex(l => l.worklistId === worklistId);
		let isFavoriteWl: boolean = index > -1;
		LogManager.debug(LogCat.RisUserContextCategories.FavoriteWorklists,
			`UserContextService.isFavorite worklistId ${worklistId} is in service favorites collection ${isFavoriteWl}`);
		return isFavoriteWl;
	}

	public setCustomWorklistFavorites(customWLFavorites: ICustomWorklistFavorites) {
		LogManager.debug(LogCat.RisUserContextCategories.CustomWorklistFavorites, "Set Custom Worklist Favorites start");

		this.userContextProxy.setCustomWorklistFavorites(customWLFavorites).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("customWLFavorites", customWLFavorites);
				LogManager.debug(LogCat.RisUserContextCategories.CustomWorklistFavorites, "Set Custom Worklist Favorites end");
			},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.CustomWorklistFavorites, "Set favorite Custom Worklist Favorites failed.", error);
			});
	}

	public setModalityAvailabilityViewerOptions(mavOptions: IModalityAvailabilityViewerOptions) {
		LogManager.debug(LogCat.LookupsCategories.ModalityAvailabilityViewer, "Set Modality Availability Viewer Options start");

		this.userContextProxy.setModalityAvailabilityViewerOptions(mavOptions).pipe(untilDestroyed(this)).subscribe(() => {
				this.processSaveResponseSuccess("modalityAvailabilityViewerOptions", mavOptions);
				LogManager.debug(LogCat.LookupsCategories.ModalityAvailabilityViewer, "Set Modality Availability Viewer Options end");
			},
			(error) => {
				LogManager.error(LogCat.LookupsCategories.ModalityAvailabilityViewer, "Set Modality Availability Viewer Options failed.", error);
			});
	}

	public setPatientLetterOptions(letterOptions: IPatientLetterOptions) {
		LogManager.debug(LogCat.RisUserContextCategories.General, "Set patient letter options start");

		this.userContextProxy.setPatientLetterOptions(letterOptions).pipe(untilDestroyed(this)).subscribe(() => {
			this.processSaveResponseSuccess("patientLetterOptions", letterOptions);
		},
			(error) => {
				LogManager.error(LogCat.RisUserContextCategories.General,
					"Set patient letter options failed.", error);
			});
	}
	//#region unused legacy code

	/*public getCurrentWorkGroupId():number{
		return this.httpProvider.userGroup;
	}*/

	/*/!*
	* Add Worklist. to Favorites
	* @param {number} key
	* *!/
	public addWorklistToFavorites(key: number): Observable<any> {
		LogManager.debug(LogCat.RisUserContextCategories.FavoriteWorklists, "UserContextService.addWorklistToFavorite");
		//get the favoriteWorklists from the cached userPreferences object and map it to array of {worklistId:number}
		let worklists: IFavoriteWorklist[] = this.getCurrFavoriteWorklists()
			.map(worklist => {
				return {worklistId: worklist.worklistId};
			});
		//add the current worklist to the array of favorites that will be sent to the server.
		worklists.push({worklistId: key});
		return this.setFavoriteWorklists(worklists, key, true);
	}*/

	/*/!*
	* Remove Worklist from Favorites
	* @param {number} key
	* *!/
	public removeWorklistFromFavorites(key: number): Observable<any> {
		LogManager.debug(LogCat.RisUserContextCategories.FavoriteWorklists, "OrganizationStructureComponent.removeOrgFromFavorite");
		let worklists: IFavoriteWorklist[] = this.getCurrFavoriteWorklists().filter(worklist => worklist.worklistId !== key); //TODO:check
		return this.setFavoriteWorklists(worklists, key, false);
	}*/

	/*// /!**
	//  * Gets the list of favorite worklists that is stored in the cached userPreferences in the service.
	//  * @returns {IFavoriteWorklist[]}
	//  *!/
	public getCurrFavoriteWorklists(): IFavoriteWorklist[] {
		let worklists: IFavoriteWorklist[] = this.getCachedUserContext().userPreferences.favoriteWorklists;
		if (worklists) {
			return worklists;
		} else {
			return [];
		}
	}*/

	/*public getUpdatedNavbarCustomWorklistsObject(worklist: InavbarCustomWorklist, operation: CollectionOperation) {
		let customWorklists = this.getCachedUserContext().userPreferences.navbarCustomWorklists;
		if (!customWorklists) {
			customWorklists = [];
		}
		const isItemInCollection: boolean = customWorklists.some(elm => elm.worklistId === worklist.worklistId);
		if (operation === "ADD") {
			if (!isItemInCollection) {
				customWorklists.push(worklist);
			}
		} else {
			if (isItemInCollection) {
				customWorklists = customWorklists.filter(elm => elm.worklistId !== worklist.worklistId);
			}
		}
		return customWorklists;
	}*/

	/*public setFavoriteWorklists(favoriteWorklists: IFavoriteWorklist[], worklistId: number, isFavorite: boolean): HttpObservable<any> {
		LogManager.debug(LogCat.RisUserContextCategories.FavoriteWorklists, "Set favorite worklists start");
		this.processSaveResponseSuccess("favoriteWorklists", favoriteWorklists);
		return this.userContextProxy.setFavoriteWorklists(favoriteWorklists).pipe(
			tap(() => {
				LogManager.debug(LogCat.RisUserContextCategories.FavoriteWorklists, "Set favorite worklists end");
			}),
			map(() => ({
				worklistId: worklistId,
				isFavorite: isFavorite
			}))
		);
	}*/

	/*public addNavBarCustomWorklists(worklistsIdArray: number[]): HttpObservable<void> {

		const worklists = worklistsIdArray.map(x => <InavbarCustomWorklist>({worklistId:x}));
		const worklistsObject: InavbarCustomWorklist[] = this.getUpdatedNavbarCustomWorklistsObjectMulti(worklists, CollectionOperation.Add);
		this.processSaveResponseSuccess("navbarCustomWorklists", worklistsObject);
		return this.userContextProxy.addNavBarCustomWorklists(worklists).pipe(tap(()=> {
			const customWlChangeData = {operation: CollectionOperation.Add, worklistIds: worklistsIdArray};
			this.customWorklistsChangeSubject.next(customWlChangeData);
		}));
	}*/

	//#endregion





}

